import { useEffect, useState } from 'react';
import { API_BASE } from '../services/api';
import './PackagePicker.css';

export const PACKAGES = [
  {
    id: 'domain_only',
    name: 'Domain Only',
    icon: 'fa-globe',
    price: 49,
    period: '/yr',
    description: 'Register a clean, ICP-ready domain with a practical DNS foundation for outreach, subdomains, and redirects.',
    features: [
      { text: 'Domain registration & ownership' },
      { text: 'DNS configuration included' },
      { text: '7-day money-back guarantee' },
      { text: 'Free transfer assistance' },
    ],
  },
  {
    id: 'domain_and_landing_page',
    name: 'Domain + Landing Page',
    icon: 'fa-rocket',
    price: 149,
    period: '/yr',
    badge: 'Popular',
    savings: 'Best value: save vs. separate purchase',
    description: 'Everything in Domain Only, plus a conversion-focused landing page you can use immediately while your main site catches up.',
    features: [
      { text: 'Everything in Domain Only' },
      { text: 'High-converting landing page', highlight: true },
      { text: 'Hosting & SSL included', highlight: true },
      { text: 'Mobile-responsive design', highlight: true },
      { text: 'SEO-optimized content' },
      { text: 'Contact form included' },
    ],
  },
  {
    id: 'domain_and_landing_page_and_video',
    name: 'Domain + Landing + Video',
    icon: 'fa-video',
    price: 200,
    period: '/yr',
    badge: 'Premium',
    savings: 'Stand out with a scroll-stopping promo video',
    description: 'Everything in the landing bundle, plus a custom 20–30s promo video for your social media and site hero — delivered within 24 hours.',
    features: [
      { text: 'Everything in Domain + Landing Page' },
      { text: 'Custom 20–30s promo video', highlight: true },
      { text: 'Vertical 9:16 for Reels, TikTok & Shorts', highlight: true },
      { text: 'Delivered within 24 hours' },
    ],
  },
];

function PackagePicker({ selectedPackage, onSelectPackage, customerPrice }) {
  const [serverConfig, setServerConfig] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/config/pricing`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setServerConfig(data);
        }
      })
      .catch(() => {}); // Fallback to hardcoded prices
  }, []);

  // Dynamic pricing: use domain-specific customerPrice when available,
  // otherwise fall back to server "from" prices or hardcoded defaults.
  const landingAddon = serverConfig?.landing_page_addon ?? 60;
  const videoAddon = serverConfig?.video_addon ?? 51;
  const domainPrice = customerPrice || serverConfig?.domain_only_from || PACKAGES[0].price;
  const bundlePrice = customerPrice
    ? customerPrice + landingAddon
    : serverConfig?.domain_and_landing_from || PACKAGES[1].price;
  const videoPrice = customerPrice
    ? customerPrice + landingAddon + videoAddon
    : serverConfig?.domain_and_landing_and_video_from || PACKAGES[2].price;

  // Merge dynamic prices with default packages
  const priceById = {
    domain_only: domainPrice,
    domain_and_landing_page: bundlePrice,
    domain_and_landing_page_and_video: videoPrice,
  };
  const packages = PACKAGES.map(pkg => ({
    ...pkg,
    price: priceById[pkg.id] ?? pkg.price,
  }));

  return (
    <section className="ppk" aria-label="Package selection">
      <div className="ppk-grid" role="radiogroup" aria-label="Choose package">
        {packages.map((pkg) => {
          const isSelected = pkg.id === selectedPackage;

          return (
            <button
              key={pkg.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={`ppk-card${pkg.badge ? ' is-featured' : ''}${isSelected ? ' is-selected' : ''}`}
              onClick={() => onSelectPackage(pkg.id)}
            >
              {pkg.badge && <span className="ppk-badge">{pkg.badge}</span>}

              <div className="ppk-card-topline">
                <div className="ppk-icon-wrap">
                  <i className={`fa-solid ${pkg.icon}`} aria-hidden="true" />
                </div>
                <span className="ppk-select-state">
                  <i className={`fa-solid ${isSelected ? 'fa-circle-check' : 'fa-circle'}`} aria-hidden="true" />
                  {isSelected ? 'Selected' : 'Select'}
                </span>
              </div>

              <div className="ppk-title-row">
                <h4 className="ppk-title">{pkg.name}</h4>
                <div className="ppk-price">
                  <span className="ppk-price-amount">${pkg.price}</span>
                  <span className="ppk-price-period">{pkg.period}</span>
                </div>
              </div>

              <p className="ppk-description">{pkg.description}</p>

              <ul className="ppk-feature-list">
                {pkg.features.map((feature, index) => (
                  <li key={`${pkg.id}-${index}`}>
                    <span className={`ppk-feature-icon${feature.highlight ? ' is-highlight' : ''}`}>
                      <i className={`fa-solid ${feature.highlight ? 'fa-star' : 'fa-check'}`} aria-hidden="true" />
                    </span>
                    {feature.highlight ? <strong>{feature.text}</strong> : feature.text}
                  </li>
                ))}
              </ul>

              <div className="ppk-footer">
                {pkg.savings && <span className="ppk-savings">{pkg.savings}</span>}
                <span className="ppk-guarantee">
                  <i className="fa-solid fa-lock" aria-hidden="true" /> PayPal Buyer Protection
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export default PackagePicker;
